import { useOnboardingStore } from '../../../store/onboardingStore';
import { Toggle } from '../ui/toggle';
import { useState, useEffect } from 'react';
import {
  Gift,
  ShoppingBag,
  ChevronUp,
  ChevronDown,
  Trash2
} from 'lucide-react';
import RenderProductFormUpsell from '../components/RenderProductFormUpsell';
import RenderProductFormBumpOffer from '../components/RenderProductFormBumpOffer';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';

export type UpsellProduct = {
  id: string;
  productName: string;
  price: string;
  description: string;
  image?: string;
  url_images?: string[];
  isManualEntry: boolean;
  productLink: string;
  externalId?: string | null;
  providerId?: string | null;
  isLoading?: boolean;
  error?: string;
};

export function StepAdditionalOffers() {
  const { language } = useAuth();
  
  const {
    data,
    updateAdditionalOffers,
    previousProducts,
    isPreviousProductsLoading,
    fetchPreviousProducts
  } = useOnboardingStore();
  const { additionalOffers } = data;

  const [upsells, setUpsells] = useState<UpsellProduct[]>(
    additionalOffers.upsell?.enabled === true && additionalOffers.upsell?.products
      ? additionalOffers.upsell.products
      : [{
        id: Date.now().toString(),
        productName: '',
        price: '',
        description: '',
        image: undefined,
        url_images: undefined,
        isManualEntry: false,
        productLink: '',
        externalId: null,
        providerId: null,
        isLoading: false,
        error: ''
      }]
  );

  const [bumpOffer, setBumpOffer] = useState({
    enabled: additionalOffers.bumpOffer?.enabled ?? false,
    productName: additionalOffers.bumpOffer?.productName || '',
    price: additionalOffers.bumpOffer?.price || '',
    description: additionalOffers.bumpOffer?.description || '',
    image: additionalOffers.bumpOffer?.image || undefined,
    isManualEntry: additionalOffers.bumpOffer?.isManualEntry ?? false,
    productLink: additionalOffers.bumpOffer?.productLink || '',
    externalId: additionalOffers.bumpOffer?.externalId || null,
    providerId: additionalOffers.bumpOffer?.providerId || null,
    url_images: additionalOffers.bumpOffer?.url_images || undefined,
    isLoading: false,
    error: ''
  });

  const [currentEditingUpsell, setCurrentEditingUpsell] = useState<string | null>(
    upsells.length > 0 ? upsells[0].id : null
  );

  const [upsellToggleEnabled, setUpsellToggleEnabled] = useState<boolean>(additionalOffers.upsell?.enabled);


  useEffect(() => {
    if (upsellToggleEnabled === false) {
      setUpsells([]);
      updateAdditionalOffers({
        ...additionalOffers,
        upsell: {
          enabled: false,
          products: []
        }
      });
    }
  }, [upsellToggleEnabled]);



  useEffect(() => {
    if (!additionalOffers.upsell) {
      updateAdditionalOffers({
        upsell: {
          enabled: true,
          products: upsells
        },
        bumpOffer: {
          enabled: false,
          productName: '',
          price: '',
          description: '',
          image: undefined,
          url_images: undefined,
          isManualEntry: false,
          productLink: ''
        }
      });
    }
  }, []);

  useEffect(() => {
    if (additionalOffers.upsell?.products) {
      setUpsells(additionalOffers.upsell.products);
    }
    if (additionalOffers.bumpOffer) {
      setBumpOffer(prev => ({
        ...prev,
        ...additionalOffers.bumpOffer
      }));
    }
  }, [additionalOffers]);

  useEffect(() => {
    fetchPreviousProducts();
  }, []);

  
  const handleUpsellToggle = (enabled: boolean) => {
    setUpsellToggleEnabled(enabled);
    handleAddUpsell();
  };

  const handleBumpOfferToggle = (enabled: boolean) => {

    if (enabled === false) {
      updateAdditionalOffers({
        ...additionalOffers,
        bumpOffer: {
          ...bumpOffer,
          enabled: false,
          productName: '',
          price: '',
          description: '',
          image: undefined,
          url_images: undefined,
          isManualEntry: false,
          productLink: ''
        }
      });
      return;
    }

    setBumpOffer({
      ...bumpOffer,
      enabled
    });

    updateAdditionalOffers({
      ...additionalOffers,
      bumpOffer: {
        ...bumpOffer,
        enabled
      }
    });
  };

  const handleImageUpload = (forUpsellId?: string) => {
    // Create a file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';

    // Handle file selection
    fileInput.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // Create a FileReader to convert the image to a data URL
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;

        if (forUpsellId) {
          // Update the upsell with the new image
          const updatedUpsells = upsells.map(upsell =>
            upsell.id === forUpsellId ? { ...upsell, image: imageUrl } : upsell
          );

          setUpsells(updatedUpsells);

          // Update store with the new image
          updateAdditionalOffers({
            ...additionalOffers,
            upsell: {
              enabled: upsellToggleEnabled,
              products: updatedUpsells
            }
          });
        } else {
          // Update order bump with the new image
          const updatedBumpOffer = {
            ...bumpOffer,
            image: imageUrl
          };
          setBumpOffer(updatedBumpOffer);

          // Update store with the new image
          updateAdditionalOffers({
            ...additionalOffers,
            bumpOffer: updatedBumpOffer
          });
        }
      };

      // Read the file as a data URL
      reader.readAsDataURL(file);
    };

    // Trigger file selection dialog
    fileInput.click();
  };

  const handleAddUpsell = () => {
    if (upsells.length < 1) {
      const newUpsell = {
        id: Date.now().toString(),
        productName: '',
        price: '',
        description: '',
        image: undefined,
        isManualEntry: false,
        productLink: '',
        externalId: null,
        providerId: null,
        isLoading: false,
        error: ''
      };
      setUpsells([...upsells, newUpsell]);
      setCurrentEditingUpsell(newUpsell.id);
      updateAdditionalOffers({
        ...additionalOffers,
        upsell: {
          enabled: true,
          products: [...upsells, newUpsell]
        }
      });
    }
  };

  const handleRemoveUpsell = (id: string) => {
    const updatedUpsells = upsells.filter(upsell => upsell.id !== id);
    setUpsells(updatedUpsells);

    updateAdditionalOffers({
      ...additionalOffers,
      upsell: {
        enabled: updatedUpsells.length > 0,
        products: updatedUpsells
      }
    });

    if (currentEditingUpsell === id) {
      setCurrentEditingUpsell(null);
    }
  };

  const handleUpsellChange = async (id: string, field: keyof UpsellProduct, value: any) => {
    const updatedUpsells = upsells.map(upsell =>
      upsell.id === id ? { ...upsell, [field]: value } : upsell
    );

    setUpsells(updatedUpsells);

    updateAdditionalOffers({
      ...additionalOffers,
      upsell: {
        enabled: true,
        products: updatedUpsells
      }
    });
  };

  const handleBumpOfferChange = async (field: string, value: any) => {
    const updatedBumpOffer = {
      ...bumpOffer,
      [field]: value,
      error: ''
    };

    setBumpOffer(updatedBumpOffer);

    updateAdditionalOffers({
      ...additionalOffers,
      bumpOffer: {
        enabled: updatedBumpOffer.enabled,
        productName: updatedBumpOffer.productName,
        price: updatedBumpOffer.price,
        description: updatedBumpOffer.description,
        image: updatedBumpOffer.image,
        isManualEntry: updatedBumpOffer.isManualEntry,
        productLink: updatedBumpOffer.productLink
      }
    });
  };

  // const handleManualEntry = (forUpsellId?: string) => {
  //   if (forUpsellId) {
  //     const updatedUpsells = upsells.map(upsell =>
  //       upsell.id === forUpsellId ? {
  //         ...upsell,
  //         isManualEntry: true,
  //         providerId: null,
  //         externalId: null,
  //         productName: '',
  //         price: '',
  //         description: '',
  //         image: undefined,
  //         productLink: ''
  //       } : upsell
  //     );
  //     setUpsells(updatedUpsells);

  //     // Update store immediately
  //     updateAdditionalOffers({
  //       ...additionalOffers,
  //       upsell: {
  //         enabled: upsellToggleEnabled,
  //         products: updatedUpsells
  //       }
  //     });

  //     if (forUpsellId !== currentEditingUpsell) {
  //       setCurrentEditingUpsell(forUpsellId);
  //     }
  //   } else {
  //     const updatedBumpOffer = {
  //       ...bumpOffer,
  //       isManualEntry: true,
  //       providerId: null,
  //       externalId: null,
  //       productName: '',
  //       price: '',
  //       description: '',
  //       image: undefined,
  //       productLink: '',
  //       url_images: undefined
  //     };
  //     setBumpOffer(updatedBumpOffer);

  //     // Update store immediately
  //     updateAdditionalOffers({
  //       ...additionalOffers,
  //       bumpOffer: updatedBumpOffer
  //     });
  //   }
  // };

  // const handleLinkEntry = (forUpsellId?: string) => {
  //   if (forUpsellId) {
  //     const updatedUpsells = upsells.map(upsell =>
  //       upsell.id === forUpsellId ? { ...upsell, isManualEntry: false } : upsell
  //     );
  //     setUpsells(updatedUpsells);

  //     if (forUpsellId !== currentEditingUpsell) {
  //       setCurrentEditingUpsell(forUpsellId);
  //     }
  //   } else {
  //     setBumpOffer({
  //       ...bumpOffer,
  //       isManualEntry: false
  //     });
  //   }
  // };

  const handleEditUpsell = (id: string) => {
    setCurrentEditingUpsell(id === currentEditingUpsell ? null : id);
  };

  const handlePreviousBumpOfferSelect = (product: any) => {
    if (!product) return;
    const updatedBumpOffer = {
      ...bumpOffer,
      enabled: true,
      productName: product.name,
      price: product.price.toString(),
      description: product.description,
      isManualEntry: false,
      image: '',
      url_images: product.images?.[0] || undefined,
      productLink: '',
      externalId: product.id,
      providerId: 'INTERNAL_FLUXI'
    };

    setBumpOffer(updatedBumpOffer);

    updateAdditionalOffers({
      ...additionalOffers,
      bumpOffer: updatedBumpOffer
    });
  };

  const handlePreviousUpsellSelect = (product: any, upsellId: string) => {
    if (!product) return;

    const updatedUpsells = upsells.map(upsell =>
      upsell.id === upsellId ? {
        ...upsell,
        productName: product.name,
        price: product.price.toString(),
        description: product.description,
        isManualEntry: false,
        image: '',
        url_images: product.images && product.images.length > 0 ? [product.images[0]] : undefined,
        productLink: '',
        externalId: product.id,
        providerId: 'INTERNAL_FLUXI'
      } : upsell
    );

    setUpsells(updatedUpsells);

    updateAdditionalOffers({
      ...additionalOffers,
      upsell: {
        enabled: true,
        products: updatedUpsells
      }
    });

    setCurrentEditingUpsell(upsellId);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-medium mb-2">✨ {dictionary["magic-title-step-3"][language]}!</h3>
        <p className="text-gray-600 mb-6">{dictionary["magic-description-step-3"][language]}</p>
      </div>

      <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-2 bg-emerald-100 rounded-full mr-3">
              <Gift className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h4 className="text-lg font-medium">{dictionary["order-bump"][language]}</h4>
              <p className="text-gray-600 text-sm">{dictionary["order-bump-description"][language]}</p>
            </div>
          </div>
          <Toggle
            enabled={bumpOffer.enabled}
            onChange={handleBumpOfferToggle}
          />
        </div>

        {bumpOffer.enabled && (
          <RenderProductFormBumpOffer
            product={additionalOffers}
            handleBumpOfferChange={handleBumpOfferChange}
            handleImageUpload={handleImageUpload}
            handlePreviousBumpOfferSelect={handlePreviousBumpOfferSelect}
            previousProducts={previousProducts}
            isPreviousProductsLoading={isPreviousProductsLoading}
          />
        )}
      </div>

      <div className="bg-gradient-to-r from-primary-50 to-pink-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-2 bg-primary-100 rounded-full mr-3">
              <ShoppingBag className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h4 className="text-lg font-medium">{dictionary["upsell-title"][language]} <span className="text-gray-500 text-sm">({dictionary["complementary-products"][language]})</span></h4>
              <p className="text-gray-600 text-sm">{dictionary["upsell-description"][language]}</p>
            </div>
          </div>
          <Toggle
            enabled={upsellToggleEnabled}
            onChange={handleUpsellToggle}
          />
        </div>

        {upsellToggleEnabled && (
          <div className="space-y-4 mt-4">
            {upsells.map((upsell, index) => (
              <div
                key={upsell.id}
                className="border border-gray-200 rounded-lg p-4 bg-white"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-md overflow-hidden mr-3">
                      {upsell.image ? (
                        <img
                          src={upsell.image}
                          alt={upsell.productName || `Upsell ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) :
                        upsell.url_images && upsell.url_images.length > 0 ? (
                          <img
                            src={upsell.url_images[0]}
                            alt={upsell.productName || `Upsell ${index + 1}`}
                            className="w-full h-full object-cover"
                          />

                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <ShoppingBag className="w-6 h-6" />
                          </div>
                        )
                      }
                    </div>
                    <div>
                      <h4 className="font-medium">
                        {upsell.productName || `Upsell ${index + 1}`}
                      </h4>
                      {upsell.price && (
                        <p className="text-sm text-gray-600">${upsell.price}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleEditUpsell(upsell.id)}
                      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-full"
                      aria-label={currentEditingUpsell === upsell.id ? "Collapse" : "Expand"}
                    >
                      {currentEditingUpsell === upsell.id ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveUpsell(upsell.id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded-full"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {currentEditingUpsell === upsell.id && (
                  <RenderProductFormUpsell
                    product={upsell}
                    isUpsell={true}
                    upsellId={upsell.id}
                    handleImageUpload={handleImageUpload}
                    upsells={upsells}
                    setUpsells={setUpsells}
                    bumpOffer={bumpOffer}
                    setBumpOffer={setBumpOffer}
                    additionalOffers={additionalOffers}
                    updateAdditionalOffers={updateAdditionalOffers}
                    upsellToggleEnabled={upsellToggleEnabled}
                    handleUpsellChange={handleUpsellChange}
                    handleBumpOfferChange={handleBumpOfferChange}
                    previousProducts={previousProducts}
                    isPreviousProductsLoading={isPreviousProductsLoading}
                    handlePreviousUpsellSelect={handlePreviousUpsellSelect}
                  />
                )}
              </div>
            ))}

            {/* {upsells.length < 1 && (
              <button
                type="button"
                onClick={handleAddUpsell}
                className="w-full py-3 border-2 border-dashed border-primary-200 rounded-lg flex items-center justify-center text-primary-600 hover:bg-primary-50 transition-colors"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add another complementary product
                <span className="text-gray-500 ml-1">({upsells.length}/1)</span>
              </button>
            )} */}
          </div>
        )}
      </div>
    </div>
  );
}